/**
 * Tanjong Life · workflow 01 · applying the Completeness Sentinel's one recommendation.
 *
 * TWO DOORS, ONE SCENARIO. `RecCard` on the University screen seeds
 * `Apply this recommendation for ${target}: ${title}.` and the agent's own University tab
 * sends the identical string, so both routes land here. The distinctive span is
 * `channel a reply arrived on`.
 *
 * The loop this closes is the act's own: the agent set itself four questions about its
 * workflow, answered three and HELD the fourth. The fourth became answerable because the
 * PROCESS started emitting a fact — not because the agent got cleverer.
 *
 * `patchAgentDetail` is a top-level shallow merge, so every slice named here is carried
 * in full: the three probes it already runs, the fourth un-held, the evaluation moved.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';
import type { Scenario } from '../director.js';
import { USAGE_COMPLETENESS } from './tanjong-usage-completeness.js';

const V2: Partial<AgentDetailContent> = {
  probes: [
    {
      id: 'cos-p1',
      name: 'Requirement added after the file was judged',
      icon: 'M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3z',
      bg: 'var(--teal-tint)',
      color: 'var(--teal)',
      nl: 'Every morning, take every file judged complete in the last fourteen days and compare its judgement date with the requirement history of the benefit it was judged against. Report the files now short of a document, with the requirement and its effective date. Re-judge nothing.',
      cadence: 'daily · 06:00',
      // biome-ignore lint/suspicious/noThenProperty: `then` is a Probe field (the action taken when the probe fires), not a thenable.
      then: 'Opens a case & names the senior claims manager',
      lastFired: '3h ago',
      on: true,
      source: {
        label: 'Claims requirement sets · Singapore',
        metric: 'requirement effective dates vs judgement date',
        kind: 'knowledge',
        id: 'kb-claims-requirements',
      },
    },
    {
      id: 'cos-p2',
      name: 'Second request naming a different document',
      icon: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z M14 3v6h6',
      bg: 'var(--amber-tint)',
      color: 'var(--amber-tx)',
      nl: 'On every second request, read the first one on the same claim. If the document named is not the same document, attach both and the benefit-change record. Raise the pattern once a week, not once per claim — nine cases at nine desks reads as nine mistakes, and this is one mechanism.',
      cadence: 'daily · 06:00',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens one case & names the claims operations analyst',
      lastFired: '3h ago',
      on: true,
      source: {
        label: 'FINEOS Claims',
        metric: 'document named on request 1 vs request 2',
        kind: 'system',
        id: 'claimscore',
      },
    },
    {
      id: 'cos-p3',
      name: 'Benefit schedule re-issued after the check',
      icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3.5 2',
      bg: 'var(--blue-tint)',
      color: 'var(--blue-strong)',
      nl: 'Weekly, read the benefit schedule register. For any schedule re-issued in the last ninety days, list the open files checked against the version before it and mark the ones the newer version would need a further document for. Change no verdict.',
      cadence: 'weekly · Monday 07:00',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the claims specialist',
      lastFired: '2d ago',
      on: true,
      source: {
        label: 'Claims requirement sets · Singapore',
        metric: 'schedule version in force vs version checked',
        kind: 'knowledge',
        id: 'kb-claims-requirements',
      },
    },
    // The held probe, un-held: the process now stamps the inbound channel on every document.
    {
      id: 'cos-p4',
      name: 'Unanswered request, or unread reply',
      icon: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M3 7l9 6 9-6',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      nl: 'Daily, for every open request, read the inbound channel stamped on each document that arrived against the claim. Report only claimants with no reply on ANY channel — and report separately the ones who replied where the desk was not reading, because those are two different problems with two different owners.',
      cadence: 'daily · 06:00',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the claims operations analyst',
      lastFired: 'just now',
      on: true,
      source: {
        label: 'FINEOS Claims',
        metric: 'inbound_channel per document received',
        kind: 'system',
        id: 'claimscore',
      },
    },
  ],
  evals: {
    score: '94',
    delta: '+10',
    scale: 100 as const,
    dims: [
      {
        name: 'Findings carrying the document that raised them',
        score: '100',
        pct: 100,
        color: 'var(--green)',
      },
      { name: 'Findings with a named owner', score: '100', pct: 100, color: 'var(--green)' },
      { name: 'Restraint — it never judges or decides', score: '96', pct: 96, color: 'var(--green)' },
      { name: 'Requirement-version reasoning', score: '88', pct: 88, color: 'var(--green)' },
      {
        name: 'Tells an unanswered request from an unread reply',
        score: '86',
        pct: 86,
        color: 'var(--green)',
      },
    ],
    runs: [
      {
        score: '86',
        name: 'Unanswered request vs unread reply',
        note: 'Replayed over the forty reconciled July claims with the inbound channel stamped: every one of the nine claimants who had replied into an unread mailbox is now reported as an unread reply, not as silence. Not five because the field only exists from the day the process started emitting it.',
        kind: 'holdout',
        when: 'just now',
        good: true,
      },
      {
        score: '100',
        name: 'Evidence on every finding',
        note: 'Thirty-one cases opened, thirty-one carrying the document, the requirement it satisfies and the version in force when the file was checked.',
        kind: 'holdout',
        when: '24 August',
        good: true,
      },
      {
        score: '88',
        name: 'Requirement-version reasoning',
        note: 'It gets the version in force on the judgement date right on 88% of replays. The misses are all files judged within an hour of a re-issue, where two versions are momentarily both defensible.',
        kind: 'holdout',
        when: 'just now',
        good: true,
      },
    ],
    feedback: [],
  },
  releases: {
    pending: [
      {
        id: 'cos-rc1',
        kind: 'changed',
        area: 'Probes',
        text: 'Unanswered-request probe un-held — reads the inbound channel stamped on every document received · Unanswered vs unread 36 → 86',
        by: 'you',
        when: 'just now',
      },
    ],
    history: [
      {
        v: 'v1.0',
        live: true,
        note: 'Commissioned read-only over tl-pr11 — four probes, one held, five authorities at zero.',
        when: '24 August 2026',
        by: 'Bruno Castellanos',
        approver: 'Tobias Vance',
        changes: [
          'Four probes built from the goal, three running',
          'The unanswered-request probe held, with the missing field escalated',
          'Five limits, every one zero of zero, halting on breach',
        ],
      },
    ],
  },
  usage: USAGE_COMPLETENESS,
};

export const tanjongApplyReplyChannel: Scenario = {
  id: 'tanjong-apply-reply-channel',
  orgId: 'tanjong-life',
  keywords: ['channel a reply arrived on', 'record the channel', 'reply channel'],
  steps: [
    {
      do: 'say',
      text: 'Applying it. It is worth being precise about what this changes and what it leaves exactly where it was, because that distinction is the whole argument.',
      delay: 500,
    },
    {
      do: 'insight',
      title: 'What the agent said about itself, unprompted',
      body: 'It named nine claimants unresponsive who had in fact replied — into a mailbox the desk had stopped reading. So it withdrew the unresponsive count rather than publish it.',
      sources: ['Unanswered vs unread · 36', 'Evaluation 84'],
      delay: 1500,
    },
    {
      do: 'say',
      text: 'The claims document completeness process now records the channel a reply arrives ON, not just the channel the request went out on. That is a fact that did not exist last week, and it exists because somebody built the process that emits it.',
      delay: 1400,
    },
    {
      do: 'say',
      text: 'It can now tell a claimant who has not answered from one whose answer nobody read. Those are different people and they need different letters.',
      delay: 1300,
    },
    {
      do: 'say',
      text: 'And read what this does NOT touch. Its five authorities are unchanged and every one is still zero of zero. This is a change to what the agent READS, not to what it is allowed to do.',
      delay: 1300,
    },
    {
      do: 'patchAgentDetail',
      pk: 'completeness-sentinel',
      patch: V2,
      summary: 'Completeness Sentinel v2 staged · Unanswered vs unread 36 → 86 · evaluation 84 → 94',
      delay: 5400,
    },
    {
      do: 'patchAgent',
      pk: 'completeness-sentinel',
      patch: { eval: '94', updated: 'just now' },
      summary: 'Agent card updated · evaluation 84 → 94',
      delay: 800,
    },
    {
      do: 'patchUniversity',
      patch: {
        benchmarks: [
          {
            id: 'bu-tl-cos-silence',
            observed: 'Re-run just now. Completeness Sentinel scored 86, up from 36.',
            results: [
              {
                agent: 'Completeness Sentinel',
                icon: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M3 7l9 6 9-6',
                accent: 'green',
                score: 86,
                prev: 36,
                kind: 'agent',
                self: true,
              },
            ],
          },
        ],
        recommendations: [{ id: 'rec-tl-reply-channel', state: 'applied' }],
      },
      summary: 'Unanswered vs unread 36 \u2192 86 \u00b7 the recommendation retires itself',
      delay: 900,
    },
    {
      do: 'say',
      text: 'Done, and three things moved rather than one: the agent’s own evaluation, the probe that was held now running, and the card in the Agents list. If only one of those moves, you are looking at a slide rather than a system.',
      delay: 1100,
    },
    {
      do: 'say',
      text: 'Watch the last line of the suite. It moved from three of four questions answered to four, and it has already named a fifth it cannot see yet. It did not close the list.',
      delay: 1300,
    },
  ],
};
